import { DataSetsProps } from '@/types';

type DatasetScope = {
  loja: number | null | undefined;
  segmento: number | null | undefined;
};

type ScopeRow = Record<string, any>;

const isPlainObject = (value: any): value is Record<string, any> =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const getFirstPresentValue = (row: ScopeRow, keys: string[]): unknown => {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(row, key)) {
      return row[key];
    }
  }
  return undefined;
};

export const parseScopeNumber = (value: unknown): number | null | undefined => {
  if (value === undefined || value === '') return undefined;
  if (value === null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const getDatasetScope = (dataset: Pick<DataSetsProps, 'COD_LOJA' | 'COD_SEGMENTO'>): DatasetScope => ({
  loja: parseScopeNumber(dataset.COD_LOJA),
  segmento: parseScopeNumber(dataset.COD_SEGMENTO),
});

export const getRowScope = (row: ScopeRow): DatasetScope => {
  if (!isPlainObject(row)) {
    return {
      loja: null,
      segmento: null,
    };
  }

  const lojaCandidate = getFirstPresentValue(row, [
    'COD_LOJA',
    'cod_loja',
    'NROEMPRESA',
    'nroempresa',
    'COD_EMPRESA',
    'cod_empresa',
  ]);
  const segmentoCandidate = getFirstPresentValue(row, [
    'COD_SEGMENTO',
    'cod_segmento',
    'NRO_SEGMENTO',
    'nro_segmento',
    'NROSEGMENTO',
    'nrosegmento',
  ]);

  return {
    loja: parseScopeNumber(lojaCandidate),
    segmento: parseScopeNumber(segmentoCandidate),
  };
};

export const rowMatchesScope = (row: ScopeRow, scope: DatasetScope): boolean => {
  const rowScope = getRowScope(row);
  if (scope.loja !== undefined && rowScope.loja !== scope.loja) return false;
  if (scope.segmento !== undefined) {
    // Alguns payloads não propagam COD_SEGMENTO para cada item; nesse caso
    // usamos apenas COD_LOJA para manter o comportamento incremental correto.
    if (rowScope.segmento !== undefined && rowScope.segmento !== scope.segmento) return false;
  }
  return scope.loja !== undefined || scope.segmento !== undefined;
};

export const mergeDataValues = (current: any, incoming: any): any => {
  if (current == null) return incoming;
  if (incoming == null) return current;

  if (Array.isArray(current) && Array.isArray(incoming)) {
    return [...current, ...incoming];
  }

  if (isPlainObject(current) && isPlainObject(incoming)) {
    const merged: Record<string, any> = { ...current };
    Object.keys(incoming).forEach((key) => {
      merged[key] = key in merged ? mergeDataValues(merged[key], incoming[key]) : incoming[key];
    });
    return merged;
  }

  return incoming;
};

export const mergeArrayByDatasetScope = (
  existing: any,
  incoming: any,
  scopes: Array<Pick<DataSetsProps, 'COD_LOJA' | 'COD_SEGMENTO'>>
): any[] => {
  const prev = Array.isArray(existing) ? existing : [];
  const next = Array.isArray(incoming) ? incoming : [];
  if (!next.length) return prev;

  const normalizedScopes = scopes.map(getDatasetScope);
  const scoped = normalizedScopes.filter((scope) => scope.loja !== undefined || scope.segmento !== undefined);
  if (!scoped.length) {
    return [...prev, ...next];
  }

  const kept = prev.filter((row) => !scoped.some((scope) => rowMatchesScope(row, scope)));
  return [...kept, ...next];
};

export const mergeByDatasetScope = (
  existing: any,
  incoming: any,
  datasets: Array<Pick<DataSetsProps, 'COD_LOJA' | 'COD_SEGMENTO'>>
): any => {
  if (incoming == null) return existing;
  if (existing == null) return incoming;

  if (Array.isArray(existing) || Array.isArray(incoming)) {
    return mergeArrayByDatasetScope(existing, incoming, datasets);
  }

  if (isPlainObject(existing) && isPlainObject(incoming)) {
    return mergeDataValues(existing, incoming);
  }

  return incoming;
};

export const shouldDownloadUpdatedDataset = (
  oldDataset: Pick<DataSetsProps, 'DT_UPDATE'> | undefined,
  newDataset: Pick<DataSetsProps, 'DT_UPDATE'>
): boolean => oldDataset?.DT_UPDATE !== newDataset.DT_UPDATE;

const toArray = <T = any>(value: any): T[] => (Array.isArray(value) ? value : []);

const mergeClientLojasByScope = (current: any[] | undefined, incoming: any[] | undefined): any[] => {
  const byKey = new Map<string, any>();
  [...toArray(current), ...toArray(incoming)].forEach((loja) => {
    if (!loja || typeof loja !== 'object') return;
    const segmentos = Array.isArray(loja.COD_SEGMENTO) ? loja.COD_SEGMENTO : [];
    const segmentosKey = segmentos
      .map((segmento: any) => Number(segmento?.cod_segmento ?? segmento?.COD_SEGMENTO ?? segmento))
      .filter((segmento) => Number.isFinite(segmento))
      .join(',');
    const key = `${Number(loja.COD_LOJA || 0)}:${segmentosKey}`;
    byKey.set(key, loja);
  });
  return Array.from(byKey.values());
};

export const mergeClientsByCodePreserveStores = (existing: any, incoming: any): any[] => {
  const prev = toArray(existing);
  const next = toArray(incoming);
  if (!next.length) return prev;

  const byClient = new Map<string, any>();
  const clientKey = (client: any) => {
    const cod = Number(client?.COD_CLIENTE);
    if (!Number.isFinite(cod)) return null;
    const loja = parseScopeNumber(client?.COD_LOJA);
    return `${cod}::${String(loja)}`;
  };
  prev.forEach((client) => {
    const key = clientKey(client);
    if (!key) return;
    byClient.set(key, client);
  });

  next.forEach((client) => {
    const key = clientKey(client);
    if (!key) return;
    const currentClient = byClient.get(key);
    if (!currentClient) {
      byClient.set(key, client);
      return;
    }

    byClient.set(key, {
      ...currentClient,
      ...client,
      lojas: mergeClientLojasByScope(currentClient.lojas, client.lojas),
    });
  });

  return Array.from(byClient.values());
};

export const mergeCatalogByProductCode = (existing: any, incoming: any): any[] => {
  const prev = toArray(existing);
  const next = toArray(incoming);
  if (!next.length) return prev;

  const getCatalogScopeLoja = (product: any): number | null | undefined => {
    const fromRoot = parseScopeNumber(product?.COD_LOJA);
    if (fromRoot !== undefined) return fromRoot;
    if (Array.isArray(product?.EMBALAGEM) && product.EMBALAGEM.length > 0) {
      const fromEmb = parseScopeNumber(product.EMBALAGEM[0]?.COD_LOJA);
      if (fromEmb !== undefined) return fromEmb;
    }
    if (Array.isArray(product?.PRECO) && product.PRECO.length > 0) {
      const fromPrice = parseScopeNumber(product.PRECO[0]?.COD_LOJA);
      if (fromPrice !== undefined) return fromPrice;
    }
    return undefined;
  };
  const productKey = (product: any) => {
    const code = Number(product?.COD_PRODUTO);
    if (!Number.isFinite(code)) return null;
    return `${code}::${String(getCatalogScopeLoja(product))}`;
  };
  const byProduct = new Map<string, any>();
  prev.forEach((product) => {
    const key = productKey(product);
    if (!key) return;
    byProduct.set(key, product);
  });
  next.forEach((product) => {
    const key = productKey(product);
    if (!key) return;
    byProduct.set(key, product);
  });

  return Array.from(byProduct.values());
};

export const mergePrecoByScope = (
  existing: any,
  incoming: any,
  datasets: Array<Pick<DataSetsProps, 'COD_LOJA' | 'COD_SEGMENTO'>>
): any => {
  const prev = isPlainObject(existing) ? existing : {};
  const next = isPlainObject(incoming) ? incoming : incoming;
  if (!isPlainObject(next)) return next;

  const scoped = datasets.map(getDatasetScope).filter((scope) => scope.loja !== undefined || scope.segmento !== undefined);
  if (!scoped.length) return mergeDataValues(prev, next);

  const clonedPrev: Record<string, any> = { ...prev };
  scoped.forEach((scope) => {
    if (scope.loja === undefined) return;
    const lojaKey = String(scope.loja);
    if (!isPlainObject(clonedPrev[lojaKey])) return;

    if (scope.segmento === undefined) {
      delete clonedPrev[lojaKey];
      return;
    }

    const segmentKey = String(scope.segmento);
    const bySegment = { ...clonedPrev[lojaKey] };
    delete bySegment[segmentKey];
    if (Object.keys(bySegment).length === 0) {
      delete clonedPrev[lojaKey];
    } else {
      clonedPrev[lojaKey] = bySegment;
    }
  });

  return mergeDataValues(clonedPrev, next);
};
